import { zodResolver } from '@hookform/resolvers/zod';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { MdArrowBack, MdSave, MdSecurity } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { ProtectedComponent } from '../../../../domain/components/rbac/ProtectedComponent';
import {
  RESOURCES_MAP,
  SYSTEM_RESOURCES,
} from '../../../../domain/env/rbac-resources';
import {
  ActionType,
  Permission,
  ResourceType,
  Role,
} from '../../../../domain/types/rbac';
import { RoleService } from '../../../../domain/use-cases/rbac/role-service';
import Button from '../../../components/button';
import Spinner from '../../../components/spinner';

// Schema de validación para edición
const editRoleSchema = z.object({
  name: z
    .string()
    .min(1, 'El nombre es requerido')
    .max(100, 'El nombre es muy largo'),
  description: z
    .string()
    .min(1, 'La descripción es requerida')
    .max(500, 'La descripción es muy larga'),
  isActive: z.boolean(),
});

type EditRoleFormData = z.infer<typeof editRoleSchema>;

export const EditRolePage: React.FC = () => {
  const { roleId } = useParams<{ roleId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingRole, setLoadingRole] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [role, setRole] = useState<Role | null>(null);
  const [selectedPermissions, setSelectedPermissions] = useState<Permission[]>(
    [],
  );

  const roleService = React.useMemo(() => new RoleService(), []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<EditRoleFormData>({
    resolver: zodResolver(editRoleSchema),
    defaultValues: {
      name: '',
      description: '',
      isActive: true,
    },
  });

  // Cargar datos del rol
  const loadRole = useCallback(async () => {
    if (!roleId) {
      toast.error('ID de rol no válido', { theme: 'colored' });
      navigate('/dashboard/roles');
      return;
    }

    try {
      setLoadingRole(true);
      const roleData = await roleService.getRoleById(roleId);

      if (roleData) {
        setRole(roleData);
        setSelectedPermissions(roleData.permissions || []);
        reset({
          name: roleData.name,
          description: roleData.description,
          isActive: roleData.isActive,
        });
      } else {
        toast.error('Rol no encontrado', { theme: 'colored' });
        navigate('/dashboard/roles');
      }
    } catch (error) {
      console.error('Error loading role:', error);
      toast.error('Error al cargar los datos del rol', { theme: 'colored' });
    } finally {
      setLoadingRole(false);
    }
  }, [roleId, roleService, navigate, reset]);

  useEffect(() => {
    loadRole();
  }, [loadRole]);

  const handlePermissionChange = (
    resource: ResourceType,
    action: ActionType,
    checked: boolean,
  ) => {
    if (checked) {
      setSelectedPermissions((prev) => {
        // Evitar duplicados
        const exists = prev.some(
          (p) => p.resource === resource && p.action === action,
        );
        if (exists) return prev;
        return [...prev, { resource, action, granted: true }];
      });
    } else {
      setSelectedPermissions((prev) =>
        prev.filter((p) => !(p.resource === resource && p.action === action)),
      );
    }
  };

  const isPermissionSelected = (
    resource: ResourceType,
    action: ActionType,
  ): boolean => {
    return selectedPermissions.some(
      (p) => p.resource === resource && p.action === action && p.granted,
    );
  };

  const handleSelectAllForResource = (
    resource: ResourceType,
    selectAll: boolean,
  ) => {
    const resourceConfig = RESOURCES_MAP[resource];
    if (!resourceConfig) return;

    if (selectAll) {
      // Agregar todos los permisos del recurso
      const newPermissions = resourceConfig.actions.map((action) => ({
        resource,
        action,
        granted: true,
      }));
      setSelectedPermissions((prev) => {
        // Remover permisos existentes del recurso y agregar los nuevos
        const filtered = prev.filter((p) => p.resource !== resource);
        return [...filtered, ...newPermissions];
      });
    } else {
      // Remover todos los permisos del recurso
      setSelectedPermissions((prev) =>
        prev.filter((p) => p.resource !== resource),
      );
    }
  };

  const getResourcePermissionCount = (resource: ResourceType): number => {
    return selectedPermissions.filter((p) => p.resource === resource).length;
  };

  const getResourceTotalActions = (resource: ResourceType): number => {
    return RESOURCES_MAP[resource]?.actions.length || 0;
  };

  const onSubmit = async (data: EditRoleFormData) => {
    if (selectedPermissions.length === 0) {
      setError('Debe seleccionar al menos un permiso');
      return;
    }

    if (!roleId) {
      setError('ID de rol no válido');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const updateData: Partial<Omit<Role, 'id'>> = {
        name: data.name,
        description: data.description,
        permissions: selectedPermissions,
        isActive: data.isActive,
        updatedAt: {
          seconds: Math.floor(Date.now() / 1000),
        },
      };

      await roleService.updateRole(roleId, updateData);
      toast.success('Rol actualizado exitosamente', { theme: 'colored' });
      navigate('/dashboard/roles');
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Error al actualizar el rol', { theme: 'colored' });
    } finally {
      setLoading(false);
    }
  };

  if (loadingRole) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <Spinner />
      </div>
    );
  }

  if (!role) {
    return null;
  }

  return (
    <ProtectedComponent resource="roles" action="update">
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <MdSecurity className="w-8 h-8 text-blue-600" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    Editar Rol
                  </h1>
                  <p className="text-gray-600">
                    Modifica la configuración y permisos del rol
                  </p>
                </div>
              </div>
              <Button
                label="Volver"
                onClick={() => navigate('/dashboard/roles')}
                icon={<MdArrowBack className="w-5 h-5" />}
                variant="secondary"
              />
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            <strong className="font-bold">Error:</strong>
            <span className="block sm:inline"> {error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Información básica */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Información del Rol
              </h3>

              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Nombre del Rol *
                  </label>
                  <input
                    type="text"
                    id="name"
                    {...register('name')}
                    disabled={role.isSystem}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                      errors.name ? 'border-red-300' : ''
                    } ${role.isSystem ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder="Ej: Editor de Contenido"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.name.message}
                    </p>
                  )}
                  {role.isSystem && (
                    <p className="mt-1 text-sm text-yellow-600">
                      Este es un rol del sistema y su nombre no puede
                      modificarse
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="description"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Descripción *
                  </label>
                  <textarea
                    id="description"
                    rows={3}
                    {...register('description')}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                      errors.description ? 'border-red-300' : ''
                    }`}
                    placeholder="Describe las responsabilidades de este rol..."
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.description.message}
                    </p>
                  )}
                </div>

                <div className="flex items-center">
                  <input
                    id="isActive"
                    type="checkbox"
                    {...register('isActive')}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="isActive"
                    className="ml-2 block text-sm text-gray-900"
                  >
                    Rol activo
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Permisos */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Permisos del Sistema
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Selecciona los permisos que tendrá este rol en cada sección del
                sistema.
              </p>

              <div className="space-y-6">
                {SYSTEM_RESOURCES.map((resource) => {
                  const resourceConfig = RESOURCES_MAP[resource.id];
                  const selectedCount = getResourcePermissionCount(resource.id);
                  const totalActions = getResourceTotalActions(resource.id);
                  const allSelected =
                    selectedCount === totalActions && totalActions > 0;

                  return (
                    <div
                      key={resource.id}
                      className="border border-gray-200 rounded-lg p-4"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={allSelected}
                              onChange={(e) =>
                                handleSelectAllForResource(
                                  resource.id,
                                  e.target.checked,
                                )
                              }
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label className="ml-2 block text-sm font-medium text-gray-900">
                              {resource.name}
                            </label>
                          </div>
                          <span className="text-xs text-gray-500">
                            ({selectedCount}/{totalActions} permisos)
                          </span>
                        </div>
                      </div>

                      <p className="text-sm text-gray-600 mb-3">
                        {resource.description}
                      </p>

                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                        {resourceConfig?.actions.map((action) => (
                          <label
                            key={action}
                            className="flex items-center space-x-2 text-sm"
                          >
                            <input
                              type="checkbox"
                              checked={isPermissionSelected(
                                resource.id,
                                action,
                              )}
                              onChange={(e) =>
                                handlePermissionChange(
                                  resource.id,
                                  action,
                                  e.target.checked,
                                )
                              }
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="text-gray-700 capitalize">
                              {action}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Botones */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  label="Cancelar"
                  onClick={() => navigate('/dashboard/roles')}
                  variant="secondary"
                />
                <Button
                  type="submit"
                  label={loading ? 'Guardando...' : 'Guardar Cambios'}
                  icon={<MdSave className="w-5 h-5" />}
                  disabled={loading}
                />
              </div>
            </div>
          </div>
        </form>
      </div>
    </ProtectedComponent>
  );
};
